import { resolveId } from '@medplum/core'
import { formatDate } from '@seen/core/dates'
import type * as appointments from '@seen/core/models/appointments/index'
import { AppointmentEncounter } from '@seen/core/models/encounters/appointment-encounter'
import { groupBy, orderBy, sortBy } from 'lodash-es'
import type { ReactNode } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import { useBulkResourceVersions } from '#src/hooks/useResourceVersions.ts'
import { AppointmentLink } from '#src/pages/participants/timeline/AppointmentLink.tsx'

export const useEncounterActivity = (
  appts: appointments.ExtendedAppointment[],
  { includeAppointmentLink = false }: { includeAppointmentLink?: boolean } = {}
): {
  action: ReactNode
  encounter: AppointmentEncounter
  key?: string
}[] => {
  const medplum = useMedplum()

  const encountersQuery = useQuery({
    queryKey: ['encounters', appts.map((a) => a.id)],
    queryFn: () =>
      Promise.all(
        appts.map((appointment) =>
          medplum.searchResourceClass(
            AppointmentEncounter,
            AppointmentEncounter.searchByAppointment(appointment.reference)
          )
        )
      ).then((results) => results.flat()),
  })

  const encounters = encountersQuery.data ?? []
  const encounterIds = encounters.map((e) => e.id)

  const { data: encounterHistory = [] } = useBulkResourceVersions(
    AppointmentEncounter,
    encounterIds
  )

  const grouped = groupBy(encounterHistory, 'id')
  const activity = Object.entries(grouped).flatMap(([_, history]) =>
    sortBy(history, 'lastUpdated').reduce<
      {
        action: ReactNode
        encounter: AppointmentEncounter
        key?: string
      }[]
    >((acc, curr) => {
      const appt = appts.find((a) => a.id === resolveId(curr.appointment))
      if (
        acc.length === 0 &&
        curr.status === 'finished' &&
        appt?.isSpecialistVisit
      ) {
        return [
          {
            action: (
              <>
                marked appointment as completed
                {appt && includeAppointmentLink && (
                  <AppointmentLink
                    className="ml-1 text-xs underline"
                    appointment={appt}
                  >
                    {appt.title}
                    {appt.start ? ` for ${formatDate(appt.start)}` : ''}
                  </AppointmentLink>
                )}
              </>
            ),
            encounter: curr,
            key: `${curr.versionId}-checkin`,
          },
        ]
      }

      const prev = acc[acc.length - 1]

      if (
        prev?.encounter.status === 'in-progress' &&
        curr.status === 'finished'
      ) {
        acc.push({
          action: (
            <>
              checked out of the appointment
              {appt && includeAppointmentLink && (
                <AppointmentLink appointment={appt}>
                  {appt.title}
                  {appt.start ? ` for ${formatDate(appt.start)}` : ''}
                </AppointmentLink>
              )}
            </>
          ),
          encounter: curr,
          key: `${curr.versionId}-checkout`,
        })
        return acc
      } else if (curr.status === 'in-progress') {
        acc.push({
          action: (
            <>
              checked in to the appointment
              {appt && includeAppointmentLink && (
                <AppointmentLink appointment={appt}>
                  {appt.title}
                  {appt.start ? ` for ${formatDate(appt.start)}` : ''}
                </AppointmentLink>
              )}
            </>
          ),
          encounter: curr,
          key: `${curr.versionId}-checkout`,
        })
        return acc
      }

      return acc
    }, [])
  )

  return orderBy(activity, (a) => a.encounter.lastUpdated, ['desc'])
}
